import { useState } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";

const icons = {
  wand: `
....X.....
...XX.....
..XX......
.XX.......
XX........
.XX.......
..XX......
...XX.....
....X.....`,
  browser: `
XXXXXXXXXX
X........X
X.XXX....X
X........X
X...XX...X
X...XX...X
X........X
X...XX...X
X...XX...X
XXXXXXXXXX`,
  brain: `
..XXXXX...
.XX...XX..
XX.....XX.
X..XXX..XX
X.XX.XX..X
X.X...XX.X
XX.....XX.
.XX...XX..
..XXXXX...`,
  code: `
XX...XX...
.XX...XX..
..XX...XX.
...XX...XX
....XX...X
...XX...XX
..XX...XX.
.XX...XX..
XX...XX...`,
  rocket: `
....X.....
...XXX....
...XXX....
..XXXXX...
..XXXXX...
.XXXXXXX..
.X.XXX.X..
..XX.XX...
.X.....X..`,
  family: `
..XX.XX.XX.
..XX.XX.XX.
...XX.XX...
....XXX....
...XXXXX...
..XXXXXXX..
.XXXXXXXXX.
XX.XXXXX.XX
.X.......X.`,
  texas: `
...XXX.....
...XXX.....
...XXXXXXX.
XXXXXXXXXX.
.XXXXXXXXXX
..XXXXXXX..
....XXXX...
.....XX....
......X....
`,
  computer: `
XXXXXXXXXX
X........X
X..XXXX..X
X..XXXX..X
X........X
XXX....XXX
..XXXXXX..
...XXXX...
....XX....`,
  globe: `
..XXXXXX..
.XX....XX.
X..XXXX..X
X.XX..XX.X
X.X....X.X
X.XX..XX.X
X..XXXX..X
.XX....XX.
..XXXXXX..`,
  ai: `
..XXXXXX..
.XX....XX.
XX..XX..XX
XX.XXXX.XX
XX.XXXX.XX
XX..XX..XX
.XX....XX.
..XXXXXX..
...XXXX...`,
  webdev: `
XXXXXXXXXX
X.XX..XX.X
X........X
X.<XX>.X.X
X........X
X.X.XX.X.X
X........X
X.XX..XX.X
XXXXXXXXXX`,
  consulting: `
...XXXX...
..XX..XX..
.XX....XX.
XX......XX
XX..XX..XX
XX..XX..XX
.XX....XX.
..XX..XX..
...XXXX...`,
  tools: `
XX....XX..
XXX..XXX..
.XXXXXX...
..XXXX....
...XX.....
..XXXX....
.XXXXXX...
XXXXXXXX..
XXXXXXXX..`,
  check: `
....XX....
...XX.....
..XX......
.XX.......
XX........
.XX.......
..XX......
...XX.....
....XX....`,
  x: `
XX....XX..
.XX..XX...
..XXXX....
...XX.....
..XXXX....
.XX..XX...
XX....XX..
X......X..
.........`,
};

const animations = {
  pulse: "animate-pixel-pulse",
  hover: "group-hover:scale-110 group-hover:rotate-3",
  spin: "group-hover:rotate-180",
  blink: "animate-pixel-blink",
  scatter: "group-hover:animate-pixel-scatter",
  glitch: "animate-pixel-glitch",
};

export default function PixelIcon({
  name,
  size = 20,
  color = "currentColor",
  className = "",
  animation = "hover",
  duration = 300,
}) {
  const [isHovered, setIsHovered] = useState(false);

  const icon = icons[name];
  if (!icon) return null;

  const rows = icon
    .trim()
    .split("\n")
    .map((row) => row.trim());

  const animationClass = animations[animation] || "";
  const transitionClass = `transition-all duration-${duration}`;

  return (
    <div
      className="group relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${rows[0].length} ${rows.length}`}
        className={`${className} ${animationClass} ${transitionClass}`}
        style={{ shapeRendering: "crispEdges" }}
      >
        {rows.map((row, y) =>
          Array.from(row).map(
            (pixel, x) =>
              pixel === "X" && (
                <motion.g
                  key={`${x}-${y}`}
                  initial={{ scale: 1, rotate: 0 }}
                  animate={
                    isHovered
                      ? {
                          scale: [1, 1.1, 1],
                          rotate: [0, (x + y) % 2 === 0 ? 3 : -3, 0],
                          x: [0, (Math.random() - 0.5) * 0.5, 0],
                          y: [0, (Math.random() - 0.5) * 0.5, 0],
                        }
                      : { scale: 1, rotate: 0, x: 0, y: 0 }
                  }
                  transition={{
                    duration: 0.8,
                    repeat: Infinity,
                    repeatType: "reverse",
                    delay: (x + y) * 0.02,
                  }}
                  className="origin-center"
                >
                  <motion.rect
                    x={x}
                    y={y}
                    width="0.8"
                    height="0.8"
                    fill={color}
                    initial={{ opacity: 1 }}
                    animate={{
                      opacity: isHovered ? [1, 0.7, 1] : 1,
                    }}
                    transition={{
                      duration: 0.4,
                      repeat: Infinity,
                      repeatType: "reverse",
                      delay: (x + y) * 0.03,
                    }}
                    className="drop-shadow-sm"
                    style={{
                      transformBox: "fill-box",
                      transformOrigin: "center",
                    }}
                  />
                </motion.g>
              )
          )
        )}
      </svg>
    </div>
  );
}

PixelIcon.propTypes = {
  name: PropTypes.string.isRequired,
  size: PropTypes.number,
  color: PropTypes.string,
  className: PropTypes.string,
  animation: PropTypes.string,
  duration: PropTypes.number,
};
